import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminTransactionsFacade,
  BulkTransitionPayloadV4,
  BulkTransitionResultV4,
} from './admin-transactions.facade';
import {
  AdminTransactionListItemV4,
  AdminTransactionsFilterV4,
  AdminTransitionDtoV4,
} from '@models/resources/admin-transaction.model';
import {
  UpdateInvoiceRecipientDto,
  UpdateInvoiceRecipientResponse,
} from '@models/resources/transaction-request.model';
import { environment } from '../../../../environments/environment';

/**
 * HTTP-Variante für die Admin-Transaktionsliste (Sprint 6 S6-5/S6-6/S6-7/S6-8).
 *
 * Bindings an V4-BE-Endpoints (Sprint 5b):
 * - `list/get`         → `GET /api/transaction-requests[/{id}]`
 * - `transition`       → `POST /api/transaction-requests/{id}/transition`
 * - `bulkTransition`   → `POST /api/transaction-requests/bulk-transition`
 *
 * Wire-Shape matched 1:1 das BE; kein Mapping nötig. Wir reichen Filter-
 * Params via `HttpParams` weiter — undefined-Werte werden vom Builder
 * automatisch weggelassen.
 */
@Injectable()
export class HttpAdminTransactionsFacade implements AdminTransactionsFacade {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/transaction-requests`;

  list(filter?: AdminTransactionsFilterV4): Observable<AdminTransactionListItemV4[]> {
    let params = new HttpParams();
    if (filter?.status && filter.status !== 'all') {
      params = params.set('status', filter.status);
    }
    if (filter?.customer_id !== undefined) {
      params = params.set('customer_id', String(filter.customer_id));
    }
    if (filter?.search?.trim()) {
      params = params.set('search', filter.search.trim());
    }
    // Sprint 8 (Sell-Workflow-UI-Split): Type-Filter (buy/sell) wird
    // pro Route gesetzt — BE liefert dann nur den passenden Slice.
    if (filter?.type) {
      params = params.set('type', filter.type);
    }
    return this.http.get<AdminTransactionListItemV4[]>(this.base, { params });
  }

  get(id: number): Observable<AdminTransactionListItemV4> {
    return this.http.get<AdminTransactionListItemV4>(`${this.base}/${id}`);
  }

  transition(
    id: number,
    dto: AdminTransitionDtoV4,
  ): Observable<AdminTransactionListItemV4> {
    return this.http.post<AdminTransactionListItemV4>(
      `${this.base}/${id}/transition`,
      dto,
    );
  }

  bulkTransition(payload: BulkTransitionPayloadV4): Observable<BulkTransitionResultV4> {
    return this.http.post<BulkTransitionResultV4>(
      `${this.base}/bulk-transition`,
      payload,
    );
  }

  updateInvoiceNote(
    id: number,
    invoiceNote: string | null,
  ): Observable<AdminTransactionListItemV4> {
    return this.http.patch<AdminTransactionListItemV4>(
      `${this.base}/${id}/invoice-note`,
      { invoice_note: invoiceNote ?? '' },
    );
  }

  updateInvoiceRecipient(
    invoiceId: number,
    payload: UpdateInvoiceRecipientDto,
  ): Observable<UpdateInvoiceRecipientResponse> {
    return this.http.patch<UpdateInvoiceRecipientResponse>(
      `${environment.apiUrl}/invoices/${invoiceId}/recipient`,
      payload,
    );
  }
}
